import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articleContent, seoArticles, shoppingGuideArticle } from '@/mocks/seoContent';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.rest';
const publisherLogo = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg';

function buildArticleJsonLd(article: {
  slug: string;
  title: string;
  shortDesc: string;
  imageUrl: string;
  sections: { id: string; title: string; content: string[] }[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.shortDesc,
    image: article.imageUrl,
    url: `${siteUrl}/article/${article.slug}`,
    datePublished: '2026-07-01',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'Yoybuy.rest',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Yoybuy.rest',
      logo: {
        '@type': 'ImageObject',
        url: publisherLogo,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
    inLanguage: 'ru',
  };
}

function getAllArticles() {
  return [shoppingGuideArticle, ...seoArticles];
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const isMainGuide = slug === 'yoybuy-spreadsheet-guide';
  const allArticles = getAllArticles();
  const matchedArticle = !isMainGuide ? allArticles.find((a) => a.slug === slug) : null;

  if (isMainGuide) {
    return <MainGuideContent />;
  }

  if (!matchedArticle) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950">Статья не найдена</h1>
            <Link to="/" className="inline-flex items-center gap-2 mt-4 px-5 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full">
              ← На главную
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.slug !== matchedArticle.slug)
    .slice(0, 3);

  return (
    <>
      <JsonLd data={buildArticleJsonLd(matchedArticle)} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-10 md:py-14">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors mb-6 md:mb-8"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              ← На главную
            </Link>

            <header className="mb-10 md:mb-14">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-[1.2] tracking-tight">
                {matchedArticle.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full">
                  Yoybuy Spreadsheet
                </span>
                <span className="text-xs text-foreground-500">Обновлено: Июль 2026</span>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[16/7] mb-10 md:mb-14">
              <img
                src={matchedArticle.imageUrl}
                alt={matchedArticle.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {matchedArticle.sections.map((section) => (
              <section key={section.id} className="mb-10 md:mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 leading-snug">
                  {section.title}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <div className="mt-8 mb-10 flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                YOYBUY SHEET
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-950 text-foreground-950 text-sm font-semibold rounded-full hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                КУПИТЬ СЕЙЧАС
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14 pt-8 border-t border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-950 mb-4">
                  Похожие статьи
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="block p-4 bg-background-100 rounded-xl hover:bg-background-200/70 transition-colors"
                    >
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-600 line-clamp-2">
                        {ra.shortDesc}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-950 mb-4">
                Полезные ссылки
              </h3>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/"
                  className="inline-block px-4 py-2 bg-background-100 text-sm text-foreground-700 rounded-full hover:bg-background-200 transition-colors"
                >
                  ← На главную Yoybuy.rest
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-background-100 text-sm text-foreground-700 rounded-full hover:bg-background-200 transition-colors"
                >
                  Yoybuy Sheet
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}

function MainGuideContent() {
  const { title, sections } = articleContent;

  const mainGuideArticleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: articleContent.metaDescription,
    image: 'https://readdy.ai/api/search-image?query=Productive%20workspace%20with%20laptop%20showing%20spreadsheet%20and%20fashion%20sneakers%20hoodie%20on%20warm%20wooden%20desk%2C%20golden%20morning%20light%20through%20window%2C%20organized%20cozy%20atmosphere%2C%20editorial%20lifestyle%20photography%2C%20warm%20beige%20tones%2C%20soft%20natural%20shadows&width=1200&height=525&seq=article-main-guide-v2-01&orientation=landscape',
    url: `${siteUrl}/article/yoybuy-spreadsheet-guide`,
    datePublished: '2026-07-01',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'Yoybuy.rest',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Yoybuy.rest',
      logo: {
        '@type': 'ImageObject',
        url: publisherLogo,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/yoybuy-spreadsheet-guide`,
    },
    inLanguage: 'ru',
  };

  return (
    <>
      <JsonLd data={mainGuideArticleLd} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-10 md:py-14">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors mb-6 md:mb-8"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              ← На главную
            </Link>

            <header className="mb-10 md:mb-14">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-[1.2] tracking-tight">
                {title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full">
                  Yoybuy Spreadsheet
                </span>
                <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full">
                  Гайд 2026
                </span>
                <span className="text-xs text-foreground-500">Обновлено: Июль 2026</span>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[16/7] mb-10 md:mb-14">
              <img
                src="https://readdy.ai/api/search-image?query=Productive%20workspace%20with%20laptop%20showing%20spreadsheet%20and%20fashion%20sneakers%20hoodie%20on%20warm%20wooden%20desk%2C%20golden%20morning%20light%20through%20window%2C%20organized%20cozy%20atmosphere%2C%20editorial%20lifestyle%20photography%2C%20warm%20beige%20tones%2C%20soft%20natural%20shadows&width=1200&height=525&seq=article-main-guide-v2-01&orientation=landscape"
                alt="Yoybuy Spreadsheet guide"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {sections.map((section, sIdx) => (
              <section key={section.id} className="mb-10 md:mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 leading-snug">
                  {section.title}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                  >
                    {paragraph}
                  </p>
                ))}

                {section.id === 'benefits' && (
                  <div className="my-8 p-5 md:p-6 bg-accent-50 rounded-2xl">
                    <p className="text-sm font-semibold text-accent-800 mb-2">
                      Ключевое преимущество:
                    </p>
                    <p className="text-sm md:text-base text-accent-900 leading-relaxed">
                      Экономия до 60% по сравнению с розничными ценами в российских магазинах при полной прозрачности всех расходов и русскоязычной поддержке на каждом этапе.
                    </p>
                  </div>
                )}

                {section.id === 'comparison' && (
                  <div className="my-8 overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b-2 border-foreground-200">
                          <th className="text-left py-3 pr-4 font-semibold text-foreground-950">Параметр</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-950">Yoybuy</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-700">Kakobuy</th>
                          <th className="text-left py-3 pl-4 font-semibold text-foreground-700">Superbuy</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-background-200/70">
                          <td className="py-3 pr-4 text-foreground-700">Комиссия</td>
                          <td className="py-3 px-4 text-foreground-950 font-medium">Фиксированная</td>
                          <td className="py-3 px-4 text-foreground-600">Переменная</td>
                          <td className="py-3 pl-4 text-foreground-600">Переменная</td>
                        </tr>
                        <tr className="border-b border-background-200/70">
                          <td className="py-3 pr-4 text-foreground-700">Скорость выкупа</td>
                          <td className="py-3 px-4 text-foreground-950 font-medium">До 24 часов</td>
                          <td className="py-3 px-4 text-foreground-600">До 48 часов</td>
                          <td className="py-3 pl-4 text-foreground-600">До 72 часов</td>
                        </tr>
                        <tr className="border-b border-background-200/70">
                          <td className="py-3 pr-4 text-foreground-700">Поддержка на русском</td>
                          <td className="py-3 px-4 text-foreground-950 font-medium">Да</td>
                          <td className="py-3 px-4 text-foreground-600">Нет</td>
                          <td className="py-3 pl-4 text-foreground-600">Нет</td>
                        </tr>
                        <tr>
                          <td className="py-3 pr-4 text-foreground-700">Доставка в РФ</td>
                          <td className="py-3 px-4 text-foreground-950 font-medium">7–14 дней</td>
                          <td className="py-3 px-4 text-foreground-600">10–20 дней</td>
                          <td className="py-3 pl-4 text-foreground-600">10–21 день</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {section.id === 'faq' && (
                  <div className="space-y-4">
                    {section.content.map((faq, fIdx) => {
                      const parts = faq.split('? ');
                      const question = parts[0] + (parts[0].endsWith('?') ? '' : '?');
                      const answer = parts.length > 1 ? parts.slice(1).join('? ') : '';
                      if (!answer) return null;
                      return (
                        <details key={fIdx} className="group bg-background-50 rounded-xl border border-background-200/70">
                          <summary className="px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-foreground-950 transition-colors list-none flex items-center justify-between">
                            {question}
                            <span className="w-5 h-5 flex items-center justify-center text-foreground-500 group-open:rotate-180 transition-transform">
                              <i className="ri-arrow-down-s-line"></i>
                            </span>
                          </summary>
                          <div className="px-5 pb-4">
                            <p className="text-sm text-foreground-700 leading-relaxed">{answer}</p>
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}

                {section.id === 'conclusion' && (
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      YOYBUY SHEET
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-up-line text-sm"></i>
                      </span>
                    </a>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-950 text-foreground-950 text-sm font-semibold rounded-full hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      КУПИТЬ СЕЙЧАС
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-up-line text-sm"></i>
                      </span>
                    </a>
                  </div>
                )}
              </section>
            ))}

            <div className="mt-14 md:mt-20 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-950 mb-4">
                Полезные ссылки
              </h3>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/"
                  className="inline-block px-4 py-2 bg-background-100 text-sm text-foreground-700 rounded-full hover:bg-background-200 transition-colors"
                >
                  ← На главную Yoybuy.rest
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-background-100 text-sm text-foreground-700 rounded-full hover:bg-background-200 transition-colors"
                >
                  Yoybuy Sheet
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}